import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LanguageSwitcher } from './components/LanguageSwitcher';
import { DynamicPluginGrid } from './components/DynamicPluginGrid';
import { usePluginCount } from './hooks/usePluginCount';
import { getServiceIcon } from './components/icons/ServiceIcons';
import { getPortfolioIcon } from './components/icons/PortfolioIcons';
import { EcosystemSection } from './components/EcosystemSection';
import { WebinarsTeaser } from './components/WebinarsTeaser';
import { SocialIcon } from './components/SocialIcons';
import { useHeroCtas, useSectionVisibility, useSocialLinks, useHeroStat } from './lib/cms';
import './App.css';

const serviceKeys = [
  'performance',
  'automation',
  'consulting',
  'strategy',
  'qa_strategy_consulting',
  'devops',
  'training',
  'writing',
  'accessibility'
];

const processSteps = ['discovery', 'audit', 'implementation', 'support'];

const portfolioItems = [
  { key: 'project_1', icon: 'rocket' },
  { key: 'project_2', icon: 'lightning' },
  { key: 'project_3', icon: 'target' },
  { key: 'project_4', icon: 'robot' },
  { key: 'project_5', icon: 'plugin' },
  { key: 'project_6', icon: 'image' }
];

const trustStats = [
  { value: '10+', labelKey: 'stat_years' },
  { value: '10k+', labelKey: 'stat_trained' },
  { value: '1M+', labelKey: 'stat_tests' },
  { value: '12', labelKey: 'stat_books' }
];

const proofCards = ['proof_1', 'proof_2', 'proof_3'];

function App() {
  const { t, i18n } = useTranslation();
  const { count: pluginCount } = usePluginCount();
  const [menuOpen, setMenuOpen] = useState(false);
  const heroCtas = useHeroCtas();
  const socials = useSocialLinks();
  const statValue = useHeroStat();
  const showSection = useSectionVisibility();
  const locale = i18n.language?.split('-')[0] ?? 'en';

  const closeMenu = () => setMenuOpen(false);

  return (
    <>
      <a href="#main-content" className="skip-to-main" data-i18n="skip_to_main">
        {t('skip_to_main')}
      </a>

      <header className="header" role="banner">
        <nav className="nav" aria-label={t('nav.main_navigation')}>
          <a href="#home" className="logo" aria-label={t('nav.logo')} onClick={closeMenu}>
            JJ
          </a>

          <LanguageSwitcher />

          <button
            className={`hamburger ${menuOpen ? 'open' : ''}`}
            onClick={() => setMenuOpen((open) => !open)}
            aria-label={t('nav.toggle_menu')}
            aria-expanded={menuOpen}
            aria-controls="primary-navigation"
            type="button"
          >
            <span aria-hidden="true"></span>
            <span aria-hidden="true"></span>
            <span aria-hidden="true"></span>
          </button>

          <ul id="primary-navigation" className={`nav-links ${menuOpen ? 'nav-open' : ''}`}>
            <li><a href="#home" onClick={closeMenu} data-i18n="nav.home">{t('nav.home')}</a></li>
            <li hidden={!showSection('about')}><a href="#about" onClick={closeMenu} data-i18n="nav.about">{t('nav.about')}</a></li>
            <li hidden={!showSection('services')}><a href="#services" onClick={closeMenu} data-i18n="nav.services">{t('nav.services')}</a></li>
            <li hidden={!showSection('portfolio')}><a href="#portfolio" onClick={closeMenu} data-i18n="nav.portfolio">{t('nav.portfolio')}</a></li>
            <li hidden={!showSection('ecosystem')}><a href="#ecosystem" onClick={closeMenu} data-i18n="nav.ecosystem">{t('nav.ecosystem')}</a></li>
            <li hidden={!showSection('plugins')}><a href="#plugins" onClick={closeMenu} data-i18n="nav.plugins">{t('nav.plugins')}</a></li>
            <li hidden={!showSection('book')}><a href="#book" onClick={closeMenu} data-i18n="nav.book">{t('nav.book')}</a></li>
            <li>
              <a href="https://thinkdifferent.blog" target="_blank" rel="noopener noreferrer" onClick={closeMenu} data-i18n="nav.blog">
                {t('nav.blog')}
              </a>
            </li>
            <li hidden={!showSection('contact')}><a href="#contact" onClick={closeMenu} data-i18n="nav.contact">{t('nav.contact')}</a></li>
          </ul>

        </nav>
        {menuOpen && <button className="nav-overlay" onClick={closeMenu} aria-label={t('nav.close_menu')} type="button" />}
      </header>

      <main id="main-content">
        <section id="home" className="hero" aria-labelledby="hero-title">
          <div className="hero-shell">
            <div className="hero-content">
              <p className="hero-kicker" data-i18n="hero.kicker">{t('hero.kicker')}</p>
              <h1 id="hero-title" data-i18n="hero.title">{t('hero.title')}</h1>
              <p className="subtitle" data-i18n="hero.subtitle">{t('hero.subtitle')}</p>
              <p className="description" data-i18n="hero.description">{t('hero.description')}</p>

              {showSection('availability') && (
                <div className="availability-badge" aria-label={t('hero.availability')}>
                  <span className="availability-dot" aria-hidden="true"></span>
                  <span data-i18n="hero.availability">{t('hero.availability')}</span>
                </div>
              )}

              <div className="cta-buttons">
                {heroCtas.map((cta) => {
                  const internal = cta.url.startsWith('#') || cta.url.startsWith('/');
                  const text = cta.title[locale] || cta.title.en || t(`hero.${cta.slug}`, { defaultValue: cta.slug });
                  const cls = cta.variant === 'primary' ? 'btn-primary' : 'btn-secondary';
                  return internal ? (
                    <a key={cta.slug} href={cta.url} className={cls} data-i18n={`hero.${cta.slug}`}>{text}</a>
                  ) : (
                    <a key={cta.slug} href={cta.url} target="_blank" rel="noopener noreferrer" className={cls} data-i18n={`hero.${cta.slug}`}>{text}</a>
                  );
                })}
              </div>

              {showSection('social') && (
                <div className="social-links" aria-label={t('hero.social_label')}>
                  {socials.map((s) => (
                    <a key={s.slug} href={s.url} target="_blank" rel="noopener noreferrer" aria-label={s.label}>
                      <span className="visually-hidden">{s.label}</span>
                      <SocialIcon name={s.icon} />
                    </a>
                  ))}
                </div>
              )}
            </div>

            {showSection('offer') && (
              <aside className="hero-card" aria-label={t('hero.offer_label')}>
                <p className="hero-card-eyebrow" data-i18n="hero.offer_label">{t('hero.offer_label')}</p>
                <h2 data-i18n="hero.offer_title">{t('hero.offer_title')}</h2>
                <p data-i18n="hero.offer_description">{t('hero.offer_description')}</p>
                <ul>
                  <li data-i18n="hero.offer_point_1">{t('hero.offer_point_1')}</li>
                  <li data-i18n="hero.offer_point_2">{t('hero.offer_point_2')}</li>
                  <li data-i18n="hero.offer_point_3">{t('hero.offer_point_3')}</li>
                </ul>
              </aside>
            )}
          </div>

          {showSection('stats') && (
            <div className="trust-stats" aria-label={t('hero.trust_stats_label')}>
              {trustStats.map((stat) => (
                <div className="trust-stat" key={stat.labelKey}>
                  <span className="trust-stat-number">{statValue(stat.labelKey)}</span>
                  <span className="trust-stat-label" data-i18n={`hero.${stat.labelKey}`}>{t(`hero.${stat.labelKey}`)}</span>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="proof-strip" aria-label={t('hero.proof_label')} hidden={!showSection('proof')}>
          <div className="container proof-grid">
            {proofCards.map((card) => (
              <article className="proof-card" key={card}>
                <span aria-hidden="true">✓</span>
                <h2 data-i18n={`hero.${card}_title`}>{t(`hero.${card}_title`)}</h2>
                <p data-i18n={`hero.${card}_desc`}>{t(`hero.${card}_desc`)}</p>
              </article>
            ))}
          </div>
        </section>

        <section id="about" className="about" aria-labelledby="about-title" hidden={!showSection('about')}>
          <div className="container">
            <p className="section-eyebrow" data-i18n="about.eyebrow">{t('about.eyebrow')}</p>
            <h2 className="section-title" id="about-title" data-i18n="about.title">{t('about.title')}</h2>
            <div className="about-content">
              <p className="about-intro" data-i18n="about.intro">{t('about.intro')}</p>
              <div className="journey">
                <h3 data-i18n="about.journey_title">{t('about.journey_title')}</h3>
                <div className="timeline">
                  {[1, 2, 3, 4].map((role) => (
                    <article className="timeline-item" key={role}>
                      <div className="timeline-marker" aria-hidden="true"></div>
                      <div className="timeline-content">
                        <h4 data-i18n={`about.role_${role}_title`}>{t(`about.role_${role}_title`)}</h4>
                        <p data-i18n={`about.role_${role}_desc`}>{t(`about.role_${role}_desc`)}</p>
                      </div>
                    </article>
                  ))}
                </div>
              </div>
              <div className="tech-stack">
                <h3 data-i18n="about.tech_stack_title">{t('about.tech_stack_title')}</h3>
                <div className="tech-tags" aria-label={t('about.tech_stack_title')}>
                  {['Architecture', 'AI Integration', 'Automation ROI', 'Performance', 'Metal', 'CUDA', 'TypeScript', 'Java', 'Python', 'Playwright', 'CI/CD', 'Accessibility', 'DevOps', 'JetBrains Plugins'].map((tech) => (
                    <span key={tech} className="tech-tag">{tech}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="services" className="services" aria-labelledby="services-title" hidden={!showSection('services')}>
          <div className="container">
            <p className="section-eyebrow" data-i18n="services.eyebrow">{t('services.eyebrow')}</p>
            <h2 className="section-title" id="services-title" data-i18n="services.title">{t('services.title')}</h2>
            <p className="section-subtitle" data-i18n="services.subtitle">{t('services.subtitle')}</p>

            <div className="services-grid">
              {serviceKeys.map((service) => (
                <article key={service} className="service-card">
                  <div className="service-icon" aria-hidden="true">
                    {getServiceIcon(service)}
                  </div>
                  <h3 data-i18n={`services.${service}.title`}>{t(`services.${service}.title`)}</h3>
                  <p data-i18n={`services.${service}.description`}>{t(`services.${service}.description`)}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="process" className="process" aria-labelledby="process-title" hidden={!showSection('process')}>
          <div className="container">
            <p className="section-eyebrow" data-i18n="process.eyebrow">{t('process.eyebrow')}</p>
            <h2 className="section-title" id="process-title" data-i18n="process.title">{t('process.title')}</h2>
            <p className="section-subtitle" data-i18n="process.subtitle">{t('process.subtitle')}</p>
            <div className="process-grid">
              {processSteps.map((step, index) => (
                <article key={step} className="process-step">
                  <div className="process-step-number" aria-hidden="true">{index + 1}</div>
                  <h3 data-i18n={`process.${step}.title`}>{t(`process.${step}.title`)}</h3>
                  <p data-i18n={`process.${step}.description`}>{t(`process.${step}.description`)}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="portfolio" className="portfolio" aria-labelledby="portfolio-title" hidden={!showSection('portfolio')}>
          <div className="container">
            <p className="section-eyebrow" data-i18n="portfolio.eyebrow">{t('portfolio.eyebrow')}</p>
            <h2 className="section-title" id="portfolio-title" data-i18n="portfolio.title">{t('portfolio.title')}</h2>
            <p className="section-subtitle" data-i18n="portfolio.subtitle">{t('portfolio.subtitle')}</p>

            <div className="portfolio-grid">
              {portfolioItems.map((item) => (
                <article className="portfolio-card" key={item.key}>
                  <div className="portfolio-icon" aria-hidden="true">
                    {getPortfolioIcon(item.icon)}
                  </div>
                  <h3 data-i18n={`portfolio.${item.key}_title`}>{t(`portfolio.${item.key}_title`)}</h3>
                  <p data-i18n={`portfolio.${item.key}_desc`}>{t(`portfolio.${item.key}_desc`)}</p>
                  <div className="portfolio-results">
                    <span className="result-tag" data-i18n={`portfolio.${item.key}_result_1`}>
                      {item.key === 'project_5'
                        ? `${pluginCount} ${t('portfolio.published_plugins')}`
                        : t(`portfolio.${item.key}_result_1`)}
                    </span>
                    <span className="result-tag" data-i18n={`portfolio.${item.key}_result_2`}>
                      {t(`portfolio.${item.key}_result_2`)}
                    </span>
                  </div>
                </article>
              ))}
            </div>

            <div className="clients-section">
              <h3 data-i18n="portfolio.clients_title">{t('portfolio.clients_title')}</h3>
              <div className="clients-list" aria-label={t('portfolio.clients_title')}>
                {['Enterprise SaaS', 'Security', 'Financial Services', 'E-commerce', 'Healthcare Tech', 'Startups', 'Logistics', 'EdTech', 'Manufacturing', 'FinTech', 'Media'].map((client) => (
                  <span className="client-tag" key={client}>{client}</span>
                ))}
              </div>
            </div>
          </div>
        </section>

        {showSection('ecosystem') && <EcosystemSection />}

        <section id="plugins" className="plugins-section" aria-labelledby="plugins-title" hidden={!showSection('plugins')}>
          <div className="container">
            <div className="plugins-header">
              <div className="plugins-heading">
                <p className="section-eyebrow" data-i18n="plugins.eyebrow">{t('plugins.eyebrow')}</p>
                <h2 className="section-title" id="plugins-title" data-i18n="plugins.title">{t('plugins.title')}</h2>
                <p className="section-subtitle" data-i18n="plugins.subtitle">{t('plugins.subtitle')}</p>
              </div>
              <a
                href="https://plugins.jetbrains.com/vendor/jakub-jirak"
                target="_blank"
                rel="noopener noreferrer"
                className="plugins-marketplace-link"
                data-i18n="plugins.cta"
              >
                {t('plugins.cta')}
              </a>
            </div>
            <DynamicPluginGrid />
          </div>
        </section>

        <section id="book" className="book-section" aria-labelledby="books-title" hidden={!showSection('book')}>
          <div className="container">
            <p className="section-eyebrow" data-i18n="books.eyebrow">{t('books.eyebrow')}</p>
            <h2 className="section-title" id="books-title" data-i18n="books.title">{t('books.title')}</h2>
            <p className="section-subtitle" data-i18n="books.subtitle">{t('books.subtitle')}</p>
            <div className="books-cta">
              <a href="https://books.jakubjirak.com" target="_blank" rel="noopener noreferrer" className="btn-primary" data-i18n="books.cta">
                {t('books.cta')}
              </a>
            </div>
          </div>
        </section>

        <WebinarsTeaser />

        <section id="contact" className="contact-section" aria-labelledby="contact-title" hidden={!showSection('contact')}>
          <div className="container">
            <p className="section-eyebrow" data-i18n="contact.eyebrow">{t('contact.eyebrow')}</p>
            <h2 className="section-title" id="contact-title" data-i18n="contact.title">{t('contact.title')}</h2>
            <p className="section-subtitle" data-i18n="contact.subtitle">{t('contact.subtitle')}</p>
            <div className="contact-cta">
              <a href="https://www.linkedin.com/in/jirakj/" target="_blank" rel="noopener noreferrer" className="btn-primary" data-i18n="contact.linkedin_cta">
                {t('contact.linkedin_cta')}
              </a>
            </div>
          </div>
        </section>
      </main>

      <footer className="footer" role="contentinfo">
        <div className="container">
          <div className="footer-tagline" data-i18n="footer.tagline">{t('footer.tagline')}</div>
          <div className="footer-links">
            <a href="https://www.amazon.com/s?i=digital-text&rh=p_27%3AJakub%2BJir%25C3%25A1k&s=relevancerank&text=Jakub+Jir%C3%A1k&ref=dp_byline_sr_ebooks_1" target="_blank" rel="noopener noreferrer" data-i18n="footer.books_on_amazon">{t('footer.books_on_amazon')}</a>
            <span className="footer-separator" aria-hidden="true">·</span>
            <a href="https://thinkdifferent.blog" target="_blank" rel="noopener noreferrer" data-i18n="nav.blog">{t('nav.blog')}</a>
            <span className="footer-separator" aria-hidden="true">·</span>
            <a href="https://www.linkedin.com/in/jirakj/" target="_blank" rel="noopener noreferrer">LinkedIn</a>
          </div>
          {/* ponytail: the year is baked into all 75 locale strings as 20xx — swap it for the current year here instead of editing every file */}
          <p data-i18n="footer.copyright">{t('footer.copyright').replace(/20\d{2}/, String(new Date().getFullYear()))}</p>
        </div>
      </footer>
    </>
  );
}

export default App;
